import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FormattedMessage, useIntl } from '../../../../../util/reactIntl';
import {
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
} from '../../../../../components';
import IconCard from '../../../../../components/SavedCardDetails/IconCard/IconCard';

import css from './LanguageMenu.module.css';

// Import translation files
import enTranslations from '../../../../../translations/en.json';
import arTranslations from '../../../../../translations/ar.json';

const LanguageMenu = ({ currentPage }) => {
  const history = useHistory();
  const location = useLocation();
  const intl = useIntl();
  const [currentLanguage, setCurrentLanguage] = useState('en');

  useEffect(() => {
    // Get saved language from localStorage
    const savedLanguage = localStorage.getItem('selectedLanguage') || 'en';
    setCurrentLanguage(savedLanguage);
    // Listen for global languageChange (footer or other triggers)
    const onLanguageChange = e => {
      const next = e?.detail?.locale === 'ar' ? 'ar' : 'en';
      setCurrentLanguage(next);
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('languageChange', onLanguageChange);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('languageChange', onLanguageChange);
      }
    };
  }, []);

  const handleLanguageChange = (language) => {
    // Save language preference to localStorage
    localStorage.setItem('selectedLanguage', language);
    setCurrentLanguage(language);

    // Update the translations dynamically
    const translations = language === 'ar' ? arTranslations : enTranslations;
    
    // Store translations in window for app.js to pick up
    window.__LANGUAGE_CHANGE__ = {
      locale: language === 'ar' ? 'ar' : 'en-US',
      messages: translations
    };

    // Keep user on the same page and only change locale & direction
    const currentPath = `${location.pathname}${location.search}${location.hash}`;
    const isOnEnglishLanding = location.pathname === '/' || currentPage === 'LandingPage';
    const isOnArabicLanding = location.pathname === '/p/ar-home' || currentPage === 'CMSPage:ar-home';

    if (language === 'ar') {
      // Navigate to Arabic landing page (route exists as '/ar-home')
      // history.push('/p/ar-home');
      // Add RTL direction for Arabic
      document.documentElement.setAttribute('dir', 'rtl');
      document.documentElement.setAttribute('lang', 'ar');
      // If currently on English LandingPage, redirect to Arabic landing page
      if (isOnEnglishLanding) {
        history.push('/p/ar-home');
        //  history.replace(currentPath);
      } else {
        history.replace(currentPath);
      }
    } else {
      // Navigate to English home page
      // history.push('/');
      // Set LTR direction for English
      document.documentElement.setAttribute('dir', 'ltr');
      document.documentElement.setAttribute('lang', 'en');
      // If currently on Arabic Landing (CMSPage), redirect to English landing page
      if (isOnArabicLanding) {
        history.push('/');
      } else {
        history.replace(currentPath);
      }
    }
    // Notify app to update IntlProvider (no reload)
    try {
      window.dispatchEvent(
        new CustomEvent('languageChange', { detail: { locale: language === 'ar' ? 'ar' : 'en' } })
      );
    } catch (_) {}
    // No full reload; avoid hydration mismatches/duplicates in production
  };

  const displayLanguage = currentLanguage === 'ar' ? 'AR' : 'EN';

  return (
    <Menu>
      <MenuLabel className={css.languageMenuLabel} isOpenClassName={css.languageMenuIsOpen}>
        <div className={css.languageSelector}>
          <IconCard type="globe" />
          <span>{displayLanguage}</span>
        </div>
      </MenuLabel>
      <MenuContent className={css.languageMenuContent}>
        <MenuItem key="english">
          <button 
            className={css.languageOption} 
            onClick={() => handleLanguageChange('en')}
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.languageEnglish" />
          </button>
        </MenuItem>
        <MenuItem key="arabic">
          <button 
            className={css.languageOption} 
            onClick={() => handleLanguageChange('ar')}
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.languageArabic" />
          </button>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

export default LanguageMenu;
